import { useState } from 'react';
import { z } from 'zod';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation, useQuery } from '@tanstack/react-query';
import { apiRequest } from '@/lib/queryClient';
import { useToast } from '@/hooks/use-toast';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Separator } from '@/components/ui/separator';
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import {
  AlertCircle,
  CheckCircle,
  Mail,
  AlertTriangle,
  Info,
  RefreshCw,
  ShieldCheck,
  ServerOff,
  Zap,
} from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Checkbox } from '@/components/ui/checkbox';
import { Skeleton } from '@/components/ui/skeleton';
import { ScrollArea } from '@/components/ui/scroll-area';
import BackButton from '@/components/ui/back-button';

const emailTestSchema = z.object({
  to: z.string().email({ message: 'Please enter a valid email address' }),
  trackLinks: z.boolean().default(false),
  includeAttachment: z.boolean().default(false),
  useHighPriority: z.boolean().default(true),
  useHtmlVersion: z.boolean().default(true),
  useSpecialMessageId: z.boolean().default(true),
});

type EmailTestValues = z.infer<typeof emailTestSchema>;

export default function EmailDiagnosticsPage() {
  const { toast } = useToast();
  const [selectedTestType, setSelectedTestType] = useState<string>('sendgrid');
  const [lastResponse, setLastResponse] = useState<any>(null);

  // Verification query
  const {
    data: verificationData,
    isLoading: verificationLoading,
    refetch: refetchVerification,
  } = useQuery({
    queryKey: ['/api/test/verify-sendgrid'],
    queryFn: async () => {
      const response = await apiRequest('GET', `/api/test/verify-sendgrid`);
      return response;
    },
  });

  // Form for test email
  const form = useForm<EmailTestValues>({
    resolver: zodResolver(emailTestSchema),
    defaultValues: {
      to: '',
      trackLinks: false,
      includeAttachment: false,
      useHighPriority: true,
      useHtmlVersion: true,
      useSpecialMessageId: true,
    },
  });

  // SendGrid Email Test Mutation
  const sendEmailMutation = useMutation({
    mutationFn: async (data: EmailTestValues) => {
      let endpoint = '/api/test/direct-sendgrid';
      let payload = {
        to: data.to,
        trackLinks: data.trackLinks,
        includeAttachment: data.includeAttachment,
        useHighPriority: data.useHighPriority,
        useHtmlVersion: data.useHtmlVersion,
        useSpecialMessageId: data.useSpecialMessageId,
      };

      const response = await apiRequest('POST', endpoint, payload);
      return response;
    },
    onSuccess: (data) => {
      console.log('Email test success:', data);
      setLastResponse(data);
      toast({
        title: 'Email Test Results',
        description: data.message || 'Email appears to have been sent successfully.',
      });
    },
    onError: (error: Error) => {
      console.error('Email test error:', error);
      toast({
        title: 'Email Test Failed',
        description: `Error: ${error.message}`,
        variant: 'destructive',
      });
    },
  });

  // Prepare verification results for display
  const getVerificationStatus = (isValid: boolean) => {
    return isValid ? (
      <Badge variant="outline" className="bg-green-50 text-green-700 border-green-200">
        <CheckCircle className="h-3 w-3 mr-1" /> Valid
      </Badge>
    ) : (
      <Badge variant="outline" className="bg-red-50 text-red-700 border-red-200">
        <AlertCircle className="h-3 w-3 mr-1" /> Invalid
      </Badge>
    );
  };

  // Submit handler for email test
  function onSubmit(data: EmailTestValues) {
    console.log('Submitting email test:', data);
    sendEmailMutation.mutate(data);
  }

  return (
    <div className="container py-10">
      <div className="mb-8 space-y-4">
        <div className="flex items-center">
          <BackButton to="/" />
          <h1 className="text-3xl font-bold tracking-tight ml-2">Email Diagnostics</h1>
        </div>
        <p className="text-muted-foreground">
          Troubleshoot email delivery issues and verify SendGrid configuration.
        </p>

        <div className="p-4 my-4 border border-green-300 bg-green-50 rounded-md">
          <h2 className="text-lg font-semibold text-green-800 mb-2 flex items-center">
            <CheckCircle className="h-5 w-5 mr-2" /> Optimized Email Delivery Active
          </h2>
          <p className="text-green-700">
            All system emails now use the following optimized delivery settings:
          </p>
          <ul className="list-disc ml-6 mt-2 text-green-700">
            <li>
              <strong>Link Tracking Disabled:</strong> Ensures email links work properly
            </li>
            <li>
              <strong>High Priority Flags:</strong> Marks messages as important for recipients
            </li>
            <li>
              <strong>Custom Message-ID:</strong> Uses authentic domain in email headers
            </li>
          </ul>
        </div>
      </div>

      <div className="grid gap-8 md:grid-cols-2">
        {/* Email Configuration Verification */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center">
              <ShieldCheck className="mr-2 h-5 w-5" />
              SendGrid Configuration
              <Button
                variant="ghost"
                size="icon"
                className="ml-auto"
                onClick={() => refetchVerification()}
                disabled={verificationLoading}
              >
                <RefreshCw className={`h-4 w-4 ${verificationLoading ? 'animate-spin' : ''}`} />
              </Button>
            </CardTitle>
            <CardDescription>
              Verify your SendGrid API key, account status, and sender authentication
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {verificationLoading ? (
              <div className="space-y-2">
                <Skeleton className="h-5 w-full" />
                <Skeleton className="h-5 w-4/5" />
                <Skeleton className="h-5 w-3/5" />
              </div>
            ) : verificationData ? (
              <div className="space-y-4">
                <div className="rounded-md border p-4">
                  <div className="flex items-center justify-between mb-2">
                    <h3 className="font-medium">API Key</h3>
                    {getVerificationStatus(verificationData?.results?.apiKeyValid)}
                  </div>
                  <div className="flex items-center justify-between mb-2">
                    <h3 className="font-medium">Account Status</h3>
                    {getVerificationStatus(verificationData?.results?.accountActive)}
                  </div>
                  <div className="flex items-center justify-between mb-2">
                    <h3 className="font-medium">Sender Verification</h3>
                    {getVerificationStatus(verificationData?.results?.senderVerified)}
                  </div>
                  <div className="flex items-center justify-between mb-2">
                    <h3 className="font-medium">Domain Authentication</h3>
                    {getVerificationStatus(verificationData?.results?.domainAuthenticated)}
                  </div>
                  <div className="flex items-center justify-between">
                    <h3 className="font-medium">Email Permissions</h3>
                    {getVerificationStatus(verificationData?.results?.emailPermissionsCorrect)}
                  </div>
                </div>

                {verificationData?.results?.issues &&
                  verificationData.results.issues.length > 0 && (
                    <Alert variant="destructive" className="mt-4">
                      <AlertCircle className="h-4 w-4" />
                      <AlertTitle>Issues Detected</AlertTitle>
                      <AlertDescription>
                        <ul className="list-disc pl-4 mt-2 space-y-1">
                          {verificationData.results.issues.map((issue: string, i: number) => (
                            <li key={i}>{issue}</li>
                          ))}
                        </ul>
                      </AlertDescription>
                    </Alert>
                  )}

                {verificationData?.results?.recommendations &&
                  verificationData.results.recommendations.length > 0 && (
                    <Alert className="mt-4">
                      <Info className="h-4 w-4" />
                      <AlertTitle>Recommendations</AlertTitle>
                      <AlertDescription>
                        <ul className="list-disc pl-4 mt-2 space-y-1">
                          {verificationData.results.recommendations.map(
                            (rec: string, i: number) => (
                              <li key={i}>{rec}</li>
                            )
                          )}
                        </ul>
                      </AlertDescription>
                    </Alert>
                  )}

                <div className="text-sm text-muted-foreground mt-4">
                  <p>
                    <strong>Verified Sending Domain:</strong>{' '}
                    {verificationData?.diagnosticInfo?.fromEmail?.split('@')[1] || 'N/A'}
                  </p>
                </div>
              </div>
            ) : (
              <Alert>
                <AlertTriangle className="h-4 w-4" />
                <AlertTitle>No Data Available</AlertTitle>
                <AlertDescription>
                  Click the refresh button to check email configuration.
                </AlertDescription>
              </Alert>
            )}
          </CardContent>
        </Card>

        {/* Send Test Email */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center">
              <Mail className="mr-2 h-5 w-5" />
              Send Test Email
            </CardTitle>
            <CardDescription>
              Send a test email to verify delivery and troubleshoot issues
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Form {...form}>
              <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
                <FormField
                  control={form.control}
                  name="to"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Recipient Email</FormLabel>
                      <FormControl>
                        <Input placeholder="email@example.com" {...field} />
                      </FormControl>
                      <FormDescription>The email address to receive the test email</FormDescription>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <div className="grid grid-cols-2 gap-4">
                  <FormField
                    control={form.control}
                    name="useHighPriority"
                    render={({ field }) => (
                      <FormItem className="flex flex-row items-start space-x-3 space-y-0 rounded-md border p-4">
                        <FormControl>
                          <Checkbox checked={field.value} onCheckedChange={field.onChange} />
                        </FormControl>
                        <div className="space-y-1 leading-none">
                          <FormLabel>High Priority</FormLabel>
                          <FormDescription>Mark email as high priority</FormDescription>
                        </div>
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="trackLinks"
                    render={({ field }) => (
                      <FormItem className="flex flex-row items-start space-x-3 space-y-0 rounded-md border p-4">
                        <FormControl>
                          <Checkbox checked={field.value} onCheckedChange={field.onChange} />
                        </FormControl>
                        <div className="space-y-1 leading-none">
                          <FormLabel>Track Links</FormLabel>
                          <FormDescription>Enable SendGrid link tracking</FormDescription>
                        </div>
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="useHtmlVersion"
                    render={({ field }) => (
                      <FormItem className="flex flex-row items-start space-x-3 space-y-0 rounded-md border p-4">
                        <FormControl>
                          <Checkbox checked={field.value} onCheckedChange={field.onChange} />
                        </FormControl>
                        <div className="space-y-1 leading-none">
                          <FormLabel>HTML Email</FormLabel>
                          <FormDescription>Include HTML version</FormDescription>
                        </div>
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="includeAttachment"
                    render={({ field }) => (
                      <FormItem className="flex flex-row items-start space-x-3 space-y-0 rounded-md border p-4">
                        <FormControl>
                          <Checkbox checked={field.value} onCheckedChange={field.onChange} />
                        </FormControl>
                        <div className="space-y-1 leading-none">
                          <FormLabel>Attachment</FormLabel>
                          <FormDescription>Include test attachment</FormDescription>
                        </div>
                      </FormItem>
                    )}
                  />
                </div>

                <Button type="submit" className="w-full" disabled={sendEmailMutation.isPending}>
                  {sendEmailMutation.isPending ? (
                    <>
                      <RefreshCw className="mr-2 h-4 w-4 animate-spin" />
                      Sending...
                    </>
                  ) : (
                    <>
                      <Zap className="mr-2 h-4 w-4" />
                      Send Test Email
                    </>
                  )}
                </Button>
              </form>
            </Form>
          </CardContent>
        </Card>
      </div>

      {/* API Response Section */}
      {lastResponse && (
        <Card className="mt-8">
          <CardHeader>
            <CardTitle className="flex items-center">
              <ServerOff className="mr-2 h-5 w-5" />
              Last API Response
            </CardTitle>
            <CardDescription>Raw response from the email service</CardDescription>
          </CardHeader>
          <CardContent>
            <ScrollArea className="h-[300px] w-full rounded-md border">
              <div className="p-4">
                <pre className="text-sm">{JSON.stringify(lastResponse, null, 2)}</pre>
              </div>
            </ScrollArea>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
